import { useCallback, useRef, useState } from 'react';
import type { Choice, OddsSelection } from '@pons/contracts';
import type { ArenaRound } from './model';
import {
  DAY,
  formatPoints,
  initialPoints,
  placePointsPrediction,
  pointsBalance,
  reducePoints,
  type PointsAction,
  type PointsState,
} from './points-model';

export function usePointsPreview() {
  const [state, setState] = useState(initialPoints);
  const current = useRef(state);
  const write = useCallback((next: PointsState) => {
    current.current = next;
    setState(next);
  }, []);
  // Commit synchronously so consecutive calls cannot spend the same balance.
  const dispatch = useCallback(
    (action: PointsAction) => {
      write(reducePoints(current.current, action));
    },
    [write],
  );
  const predict = useCallback(
    (
      round: ArenaRound,
      choice: Choice,
      now: number,
      selection?: OddsSelection,
    ) => {
      const day = Math.floor(Date.now() / DAY) + current.current.dayOffset;
      const result = placePointsPrediction(
        current.current,
        round,
        choice,
        now,
        selection,
        day,
      );
      if (result.ok) write(result.state);
      return result;
    },
    [write],
  );
  const settle = useCallback(
    (rounds: ArenaRound[], now: number) => {
      const day = Math.floor(Date.now() / DAY) + current.current.dayOffset;
      const next = reducePoints(current.current, {
        type: 'settle',
        rounds,
        now,
        day,
      });
      if (next !== current.current) write(next);
    },
    [write],
  );
  const balanceCents = pointsBalance(state);
  const committedCents = Object.values(state.positions)
    .filter((position) => position.status === 'pending')
    .reduce((sum, position) => sum + position.stakeCents, 0);
  return {
    state,
    dispatch,
    predict,
    settle,
    balanceCents,
    committedCents,
    formattedBalance: formatPoints(balanceCents),
  };
}
export type PointsPreview = ReturnType<typeof usePointsPreview>;
